import camelCase from "lodash/camelCase";
import debounce from "lodash/debounce";
import snakeCase from "lodash/snakeCase";
import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useReducer, useState } from "react";
import { Divider, Form } from "semantic-ui-react";
import Addresses from "components/forms/addresses/Addresses";

import RuvixxForm from "components/RuvixxForm";
import RuvixxSelect from "components/RuvixxSelect";
import CaseService from "services/Case";
import EntityService from "services/Entity";
import UserService from "services/User";
import RegionService from "services/Regions";
import SourceStatusService from "services/SourceStatus";
import ProductLineService from "services/ProductLine";
import { filterUsersForDropdown } from "helpers/user";

import CustomFieldForm from "components/forms/CustomFieldForm";
import CustomFieldService from "services/CustomField";
import { extractCustom, getCustomFieldsForSubmit } from "components/helpers";
import { getCountryByCode } from "helpers/countries";
import { TagFormField } from "components/TagFormField";
import tagConstants from "constants/Tag";

const formReducer = (s, a) => ({ ...s, ...a });
const initializer = model => {
  return {
    id: model?.id,
    name: "",
    entityId: null,
    investigationStatusId: null,
    website: [],
    industry: "",
    companyDescription: "",
    actionableDomain: "",
    regionId: null,
    sourceStatusId: null,
    productLineId: null,
    addresses: [],
    custom: [],
    customFields: [],
    caseInvestigators: [],
    tags: [],
  };
};

function CaseForm({ caseId, onSuccess, model }) {
  const [formData, setFormData] = useReducer(formReducer, model, initializer);
  const [allUsers, setAllUsers] = useState([]);
  const [investigationStatuses, setInvestigationStatuses] = useState([]);
  const [regionsOptions, setRegionsOptions] = useState([]);
  const [countriesOptions, setContriesOptions] = useState([]);
  const [sourceStatuses, setSourceStatuses] = useState([]);
  const [productLineOptions, setProductLineOptions] = useState([]);
  const [customFieldConfigs, setCustomFieldConfigs] = useState([]);
  const [custom, setCustom] = useState([]);
  const [websiteOptions, setWebsiteOptions] = useState([]);
  const [customFieldsLoaded, setCustomFieldsLoaded] = useState(false);

  const fetchCase = useCallback(async caseId => {
    const resp = await CaseService.get(caseId);
    const newFormData = { ...initializer(null) };
    Object.keys(resp).forEach(key => {
      newFormData[camelCase(key)] = resp[key];
    });
    newFormData["caseInvestigators"] = (
      newFormData["caseInvestigators"] || []
    ).map(({ id }) => id);
    // Parse comma-separated website string into array for Form.Select
    if (!Array.isArray(newFormData["website"])) {
      newFormData["website"] = newFormData["website"]
        ? newFormData["website"]
            .split(",")
            .map(u => u.trim())
            .filter(Boolean)
        : [];
    }
    setFormData(newFormData);
    setCustom(extractCustom(newFormData.customFields));
  }, []);

  const fetchUsers = async () => {
    const users = await UserService.getUsers();
    setAllUsers(users);
  };

  // Re-filter user options when allUsers or caseInvestigators change
  const users = useMemo(
    () => filterUsersForDropdown(allUsers, formData.caseInvestigators),
    [allUsers, formData.caseInvestigators]
  );

  const fetchInvestigationStatuses = async () => {
    const statuses = (
      await CaseService.getInvestigationStatusesForFilters()
    ).map(({ id, name }) => ({
      key: id,
      text: name,
      value: id,
    }));
    setInvestigationStatuses(statuses);
  };

  const fetchCustomFieldConfigs = useCallback(async () => {
    let customFieldConfigs =
      await CustomFieldService.getCustomFieldConfigsForModel("Case");
    setCustomFieldsLoaded(true);
    setCustomFieldConfigs(customFieldConfigs);
  }, []);

  const handleUpdateCustomFieldConfigs = customFieldConfigs => {
    setCustomFieldConfigs(customFieldConfigs);
    const newCustomFields = customFieldConfigs
      .filter(cfc => cfc.required || cfc.selected)
      .map(cfc => ({ key: cfc.label, value: cfc.value }));
    setCustom(newCustomFields);
  };

  const fetchRegions = useCallback(async () => {
    const regions = await RegionService.getRegions();
    const options = regions.map(({ id, name }) => ({
      key: id,
      value: id,
      text: name,
    }));
    setRegionsOptions(options);
  }, []);

  useEffect(() => {
    const prepareCountriesSelect = async () => {
      const region = await RegionService.getRegion(formData.regionId);
      const options = region?.countries.map(code => ({
        key: code,
        value: code,
        text: getCountryByCode(code).text,
      }));
      setContriesOptions(options);

      if (region?.countries.indexOf(formData.country) < 0) {
        setFormData({ country: null });
      }
    };

    if (!formData.regionId) {
      setFormData({ country: null });
      setContriesOptions([]);
      return;
    }

    prepareCountriesSelect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.regionId]);

  const fetchSourceStatuses = useCallback(async () => {
    const sourceStatuses = await SourceStatusService.getForFilters();
    const options = sourceStatuses.map(({ id, name }) => ({
      key: id,
      value: id,
      text: name,
    }));
    setSourceStatuses(options);
  }, []);

  const fetchProductLines = useCallback(async () => {
    const productLines = await ProductLineService.getForFilters();
    const options = productLines.map(({ id, name }) => ({
      key: id,
      value: id,
      text: name,
    }));
    setProductLineOptions(options);
  }, []);

  // Fetch static dropdown data once on mount
  useEffect(() => {
    fetchUsers();
    fetchInvestigationStatuses();
    fetchRegions();
    fetchSourceStatuses();
    fetchProductLines();
  }, [fetchRegions, fetchSourceStatuses, fetchProductLines]);

  // Fetch case data only when editing
  useEffect(() => {
    if (caseId) {
      fetchCase(caseId);
    }
  }, [caseId, fetchCase]);

  // Fetch custom field configs on mount
  useEffect(() => {
    fetchCustomFieldConfigs();
  }, [fetchCustomFieldConfigs]);

  const handleWebsiteSearchChange = useMemo(
    () =>
      debounce(async (_e, { searchQuery }) => {
        if (!searchQuery) {
          setWebsiteOptions([]);
          return;
        }
        const results = await CaseService.getWebsitesForFilters({
          search_query: searchQuery,
        });
        setWebsiteOptions(
          results.map(({ name }) => ({ key: name, value: name, text: name }))
        );
      }, 300),
    []
  );

  useEffect(() => {
    return () => {
      handleWebsiteSearchChange.cancel();
    };
  }, [handleWebsiteSearchChange]);

  const handleChange = (_, { name, type, checked, value }) => {
    if (!name) return;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const updateAddresses = addresses => {
    setFormData({ ...formData, addresses: addresses });
  };

  const handleTagChange = (value = []) => {
    setFormData({ tags: value });
  };

  const handleSubmit = async _e => {
    const cust = getCustomFieldsForSubmit(customFieldConfigs, custom);
    const formDataWithCustomFields = { ...formData, customFields: cust };
    const data = {};
    Object.keys(formDataWithCustomFields).forEach(key => {
      let value = formDataWithCustomFields[key];

      // Keep website as array (empty array when cleared)
      if (key === "website") {
        data[snakeCase(key)] = value && value.length > 0 ? value : [];
        return;
      }

      data[snakeCase(key)] = value || null;
    });

    try {
      if (caseId) {
        await CaseService.update(caseId, data);
      } else {
        await CaseService.create(data);
      }
    } catch (error) {
      // Handle different error formats
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Failed to save case. Please check your inputs and try again.";
      throw new Error(message);
    }
  };

  const createEntity = useCallback(
    async name => {
      return EntityService.createEntity({
        name,
        region_id: formData.regionId,
        country: formData.country,
      });
    },
    [formData.regionId, formData.country]
  );

  return (
    <RuvixxForm
      ready={!!formData.name && !!formData.productLineId && customFieldsLoaded}
      onSubmit={handleSubmit}
      onSuccess={onSuccess}
    >
      <Form.Input
        inline
        required
        label="Name"
        name="name"
        value={formData.name}
        onChange={handleChange}
      />
      <RuvixxSelect
        label="Entity"
        name="entityId"
        clearable
        allowAdditions
        createFn={createEntity}
        value={formData.entityId}
        onChange={handleChange}
        queryFn={EntityService.getEntitiesForFilters}
        setValue={value =>
          setFormData({ ...formData, entityId: value?.id || null })
        }
      />
      <Form.Select
        selectOnBlur={false}
        label="Investigators"
        inline
        clearable
        search
        selection
        multiple
        name="caseInvestigators"
        options={users}
        value={formData.caseInvestigators}
        onChange={handleChange}
      />
      <Form.Select
        selectOnBlur={false}
        label="Investigation Status"
        inline
        clearable
        search
        selection
        name="investigationStatusId"
        options={investigationStatuses}
        value={formData.investigationStatusId}
        onChange={handleChange}
      />
      <Form.Select
        selectOnBlur={false}
        inline
        clearable
        search
        multiple
        label="Website"
        name="website"
        placeholder="Add website URL..."
        options={[
          ...(formData.website || []).map(url => ({
            key: url,
            value: url,
            text: url,
          })),
          ...websiteOptions.filter(
            opt => !(formData.website || []).includes(opt.value)
          ),
        ]}
        value={formData.website || []}
        onChange={handleChange}
        onSearchChange={handleWebsiteSearchChange}
        allowAdditions
      />
      <Form.Input
        inline
        label="Industry"
        name="industry"
        value={formData.industry}
        onChange={handleChange}
      />
      <Form.Input
        inline
        label="Company Description"
        name="companyDescription"
        value={formData.companyDescription}
        onChange={handleChange}
      />
      <Form.Input
        inline
        label="Actionable Domain"
        name="actionableDomain"
        value={formData.actionableDomain}
        onChange={handleChange}
      />
      <Form.Select
        selectOnBlur={false}
        inline
        label="Region"
        name="regionId"
        search
        clearable
        options={regionsOptions}
        value={formData.regionId}
        onChange={handleChange}
      />
      <Form.Select
        selectOnBlur={false}
        inline
        label="Country"
        name="country"
        disabled={!formData.regionId}
        search
        clearable
        options={countriesOptions}
        value={formData.country}
        onChange={handleChange}
      />
      <Form.Select
        selectOnBlur={false}
        inline
        label="Source Status"
        name="sourceStatusId"
        search
        clearable
        options={sourceStatuses}
        value={formData.sourceStatusId}
        onChange={handleChange}
      />
      <Form.Select
        selectOnBlur={false}
        inline
        required
        clearable
        search
        label="Product Line"
        name="productLineId"
        options={productLineOptions}
        value={formData.productLineId}
        onChange={handleChange}
        placeholder="Choose a Product Line"
      />
      <Addresses
        addresses={formData.addresses}
        updateAddresses={updateAddresses}
      />
      <Divider hidden />
      <TagFormField
        modelType={tagConstants.taggableModels.case}
        formValue={formData.tags}
        onChange={handleTagChange}
      />
      {customFieldConfigs && (
        <CustomFieldForm
          modelType="Case"
          customFields={custom}
          customFieldConfigs={customFieldConfigs}
          onUpdateCustomFieldConfigs={handleUpdateCustomFieldConfigs}
        />
      )}
    </RuvixxForm>
  );
}

CaseForm.propTypes = {
  onSuccess: PropTypes.func.isRequired,
};

export default CaseForm;
